import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useApprovalsQueries } from './hooks/useApprovalsQueries';
import { useApprovalsCount } from './hooks/useApprovalsCount';
import ApprovalPostItem from './components/ApprovalPostItem';
import ApprovalPostItemSkeleton from './components/ApprovalPostItemSkeleton';
import ApprovalsEmptyState from './components/ApprovalsEmptyState';
import dayjs from 'dayjs';
import { ActivityIndicator, Alert, Chip, Tabs } from '@kontentino/ui';
import { GetPostsParams } from 'app/modules/posts/api/posts';
import {
  usePage,
  useIsPagesLoading,
  useShouldLoadPages,
} from 'modules/page/pageSelector';
import { useDispatch } from 'react-redux';
import { PageThunk } from 'modules/page/pageActions';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { UserPermission } from 'constants/userPermission';
import ApprovalsModals from 'components/modals/ApprovalsModals';
import { useTranslation } from 'react-i18next';
import { OrderDirection } from 'app/types';
import ApprovalsSortDropdown from './components/ApprovalsSortDropdown';
import ApprovedPostsDropdown from './components/ApprovedPostsDropdown';
import { PostsContainerProvider } from 'components/posts/context';
import { ApprovedPostsProvider } from './context/ApprovedPostsContext';
import { PostDetailWithDate } from 'types/PostDetail';
import { PostsState } from 'components/posts/Container';
import { useActions } from 'app/hooks/useActions';
import { useG2ReviewTrigger } from 'app/hooks/useG2ReviewTrigger';
import { ActionButton } from 'app/components/actions/ActionButton';

const POSTS_PER_PAGE = 10;

type ApprovalTab = 'internal' | 'client';

const STATUS_BY_TAB: Record<ApprovalTab, number[]> = {
  internal: [STATUS.WAITING_FOR_INTERNAL_APPROVAL],
  client: [STATUS.AT_CLIENT],
};

const Approvals: FC = () => {
  const dispatch = useDispatch();
  const { pages } = usePage();
  const shouldLoadPages = useShouldLoadPages();
  const { hasSome } = useUserPermissions();
  const canViewInternalApprovals = hasSome(
    UserPermission.POST_COMMUNICATION_INTERNAL_SHOW,
  );
  const { t } = useTranslation();
  const { userActions } = useActions('users');

  const [activeTab, setActiveTab] = useState<ApprovalTab>(
    canViewInternalApprovals ? 'internal' : 'client',
  );
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(
    OrderDirection.Desc,
  );

  useEffect(() => {
    if (!canViewInternalApprovals && activeTab === 'internal') {
      setActiveTab('client');
    }
  }, [canViewInternalApprovals, activeTab, setActiveTab]);

  useEffect(() => {
    if (shouldLoadPages) {
      dispatch(PageThunk.requestPages());
    }
  }, [dispatch, shouldLoadPages]);

  const profileIds = useMemo(() => pages.map((page) => page.id), [pages]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as ApprovalTab);
  };

  const {
    internalTotalCount,
    clientTotalCount,
    isLoading: isCountsLoading,
  } = useApprovalsCount();

  const params: Omit<GetPostsParams, 'page'> = useMemo(() => {
    const effectiveTab = !canViewInternalApprovals ? 'client' : activeTab;

    return {
      dateFrom: dayjs().subtract(6, 'months').format('YYYY-MM-DD'),
      dateTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
      profiles: profileIds,
      profileGroups: [],
      statuses: STATUS_BY_TAB[effectiveTab],
      labels: [],
      assignedUsers: [],
      pseudoTypes: [],
      projects: [],
      limit: POSTS_PER_PAGE,
      orderDirection,
    };
  }, [profileIds, activeTab, canViewInternalApprovals, orderDirection]);

  const {
    posts,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    sentryRef,
    rootRef,
    isFetching,
  } = useApprovalsQueries(params);

  const { triggerG2Review } = useG2ReviewTrigger();
  const hadPostsRef = useRef(false);

  useEffect(() => {
    if (!isLoading && posts.length > 0) {
      hadPostsRef.current = true;
    }
  }, [isLoading, posts.length]);

  useEffect(() => {
    hadPostsRef.current = false;
  }, [activeTab]);

  useEffect(() => {
    if (!isLoading && posts.length === 0 && hadPostsRef.current) {
      hadPostsRef.current = false;
      triggerG2Review();
    }
  }, [isLoading, posts.length, triggerG2Review]);

  const isPagesLoading = useIsPagesLoading();
  const shouldShowCounts =
    !isCountsLoading && !isPagesLoading && profileIds.length > 0;
  const isSortDropdownDisabled =
    isLoading ||
    isPagesLoading ||
    (activeTab === 'internal'
      ? internalTotalCount === 0
      : clientTotalCount === 0);

  return (
    <ApprovedPostsProvider>
      <PostsContainerProvider
        value={
          {
            posts: {
              allPosts: posts,
              postsWithDate: posts.filter(
                (post) => post.date !== null,
              ) as PostDetailWithDate[],
              postIdeas: posts.filter((post) => post.date === null),
              groupedByDate: {},
            },
            pages,
            isPagesLoading,
            activeState: {
              profiles: pages,
              profilesIds: pages.map((page) => page.id),
            },
          } as PostsState
        }
      >
        <div className="tw-h-full tw-overflow-auto" ref={rootRef}>
          <div className="tw-flex tw-min-h-full tw-flex-col tw-gap-4 tw-p-4">
            <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-5xl tw-flex-1 tw-flex-col">
              <div className="bg-gradient-primary tw-sticky tw-top-0 tw-z-[11] tw-mb-4 tw-pt-4">
                <div className="tw-mb-4 tw-flex tw-items-center  tw-justify-between tw-gap-3">
                  <h1 className="tw-text-base tw-font-semibold tw-text-grayscale-180 md:tw-text-lg">
                    {t('pendingApprovals')}
                  </h1>
                  <ActionButton
                    action={userActions.inviteUser}
                    data-name="approvals-topbar-invite-user"
                    data-cy="approvals-topbar-invite-user"
                    variant="secondary"
                    size="small"
                  />
                  {!canViewInternalApprovals && (
                    <div className="tw-ml-auto">
                      <ApprovalsSortDropdown
                        selectedSortOption={orderDirection}
                        onSortOptionSelect={setOrderDirection}
                        disabled={isSortDropdownDisabled}
                      />
                    </div>
                  )}
                </div>
                <div
                  className={
                    'tw-flex tw-items-center tw-justify-between tw-border-b tw-border-grayscale-10'
                  }
                >
                  {canViewInternalApprovals && (
                    <>
                      <Tabs.Root
                        value={activeTab}
                        onValueChange={handleTabChange}
                        className="tw-flex-1"
                      >
                        <Tabs.List>
                          <Tabs.Tab
                            value="internal"
                            disabled={isLoading || isPagesLoading || isFetching}
                            data-name="approvals-internal-tab"
                            data-cy="approvals-internal-tab"
                            className="md:tw-pb-3"
                          >
                            {t('threads.internal')}
                            {shouldShowCounts && (
                              <Chip>{internalTotalCount}</Chip>
                            )}
                          </Tabs.Tab>
                          <Tabs.Tab
                            value="client"
                            disabled={isLoading || isPagesLoading || isFetching}
                            data-name="approvals-client-tab"
                            data-cy="approvals-client-tab"
                            className="md:tw-pb-3"
                          >
                            {t('threads.client')}
                            {shouldShowCounts && (
                              <Chip>{clientTotalCount}</Chip>
                            )}
                          </Tabs.Tab>
                        </Tabs.List>
                      </Tabs.Root>
                      <div className="tw-ml-auto">
                        <ApprovalsSortDropdown
                          selectedSortOption={orderDirection}
                          onSortOptionSelect={setOrderDirection}
                          disabled={isSortDropdownDisabled}
                        />
                      </div>
                    </>
                  )}
                </div>
                <ApprovedPostsDropdown
                  activeTab={!canViewInternalApprovals ? 'client' : activeTab}
                />
              </div>
              {isPagesLoading || isLoading ? (
                <div
                  className="tw-flex tw-w-full tw-flex-col tw-gap-y-10 tw-px-1 tw-py-6 md:tw-gap-6"
                  data-name="approvals-skeleton-list"
                >
                  {Array.from({ length: 3 }).map((_, index) => (
                    <ApprovalPostItemSkeleton key={index} />
                  ))}
                </div>
              ) : error ? (
                <div className="tw-flex tw-h-full tw-flex-1 tw-flex-col tw-justify-center tw-pt-6">
                  <Alert variant="danger">{t('failedToLoadPosts')}</Alert>
                </div>
              ) : posts.length === 0 ? (
                <ApprovalsEmptyState />
              ) : (
                <>
                  <div
                    className="tw-flex tw-w-full tw-flex-col tw-gap-y-12 tw-px-1 tw-py-6 md:tw-gap-y-16"
                    data-name="approvals-posts-list"
                  >
                    {posts.map((post) => (
                      <ApprovalPostItem key={post.id} post={post} />
                    ))}
                  </div>

                  {hasNextPage && (
                    <div ref={sentryRef} className="tw-py-4 tw-text-center">
                      {isFetchingNextPage && <ActivityIndicator />}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <ApprovalsModals />
        </div>
      </PostsContainerProvider>
    </ApprovedPostsProvider>
  );
};

export default Approvals;
